import { observer } from "mobx-react";
import { observable, computed, action, autorun, toJS } from "mobx";
import { config } from "src/config";
import { authStore } from 'src/modules/auth';
import RichTextEditor from 'react-rte';

interface IAppSetting {
    app_setting_id: number;
    SettingName: string;
    SettingCategory: string;
    SettingType: string;
    SettingDisplayName: string;
    SettingLastUpdate: string;
    SettingDescription: string;
    SettingValue: string;
    dev_only: number;
    edited: boolean;
}

interface IEmailTemplate {
    email_template_id: number;
    EmailTemplateName: string;
    EmailTemplateReference: string;
    Sections: IEmailTemplateSection[];
    Variables: IEmailVariable[];
}

interface IEmailTemplateSection {
    email_template_section_id: number
    email_template_id: number;
    EmailSectionName: string;
    EmailSectionContent: string;
    EmailSectionReference: string;
    RTEValue: string | any;
}

interface IEmailVariable {
    email_template_variable_id: number;
    email_template_id: number;
    EmailTemplateVariable: string;
    EmailTemplateVariableDescription: string;
}

class SettingsStore {
    @observable
    public AppSettings: IAppSetting[];

    @observable
    public app_settings_loaded: boolean = false;

    @observable
    public EmailTemplates: any[];

    @observable
    public email_templates_loaded: boolean = false;

    @observable
    public rte_loaded: boolean = false;

    @observable
    public template_saved: boolean = false;

    @observable
    public active_template_id: number;
    
    @observable
    public settings_save_valid: boolean = false;

    @observable
    public noCompanyUserList: any[];

    @observable
    public no_company_user_list_loaded: boolean = false;

    @action 
    public updateSetting = (setting_id: number, newValue: any) => {
        this.settings_save_valid = true
        this.AppSettings = this.AppSettings.map((setting: IAppSetting) => {
            if (setting.app_setting_id === setting_id) {
                return {
                    ...setting,
                    SettingValue: newValue,
                    edited: true
                }
            }
            return setting
        })
    };

    @action 
    public saveSettings = async () => {
        const response: Response = await fetch(
            config.BASE_URL + "/api/settings/save_app_settings",
            {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: JSON.stringify({
                    settings: this.AppSettings
                }),
            }
        );
        const result = await response.json();
        this.AppSettings = result.body;
        this.settings_save_valid = false;
        return result.success;
    }

    @action
    public getEmailTemplates = async () => {
        const response: Response = await fetch(
            config.BASE_URL + "/api/settings/email_templates",
            {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: authStore.currentUser.SessionKey || "",
                },
                method: "GET",
            }
        );
        const responseJson = await response.json();
        this.EmailTemplates = responseJson.body
            .map((template: IEmailTemplate) => {
                return {
                    ...template,
                    Sections: template.Sections.map((section: IEmailTemplateSection) => {
                        return {
                            ...section,
                            RTEValue: RichTextEditor.createValueFromString(section.EmailSectionContent, 'html')
                        }
                    })
                }
            })
        if (responseJson.body.length === 0) {
            this.active_template_id = 0
        }
        else {
            this.active_template_id = responseJson.body[0].email_template_id;
        } 
        this.email_templates_loaded = true;
    };

    @action
    public saveEmailTemplate = async (emailTemplate: IEmailTemplate) => {
        const response: Response = await fetch(
            config.BASE_URL + "/api/settings/save_email_template",
            {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: JSON.stringify({
                    emailTemplate
                }),
            }
        );
        const result = await response.json();
        this.template_saved = true;
        return result.success;
    }

    @action
    public getAppSettings = async () => {
        const response: Response = await fetch(
            config.BASE_URL + "/api/settings/app_settings",
            {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: authStore.currentUser.SessionKey || "",
                },
                method: "GET",
            }
        );
        const result = await response.json();
        this.AppSettings = result.body;
        this.app_settings_loaded = true;
    }

    @action
    public getNoCompanyUserList = async () => {
        const response: Response = await fetch(
            config.BASE_URL + "/api/settings/no_company_users",
            {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: authStore.currentUser.SessionKey || "",
                },
                method: "GET",
            }
        );
        const result = await response.json();
        this.noCompanyUserList = result.body;
        this.no_company_user_list_loaded = true;
    }

    @action
    public saveNoCompanyUser = async (user: any) => {
        const response: Response = await fetch(
            config.BASE_URL + "/api/settings/save_no_company_user",
            {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: JSON.stringify({
                    user
                }),
            }
        );
        const result = await response.json();
        this.noCompanyUserList = this.noCompanyUserList.filter((u: any) => u.auth_id !== user.auth_id)
    }

}

export default new SettingsStore();


// WEBPACK FOOTER //
// ./src/modules/admin/modules/Settings/settingsStore.ts